import { inject, Injectable } from '@angular/core';
import { combineQueries, EntityUIQuery, HashMap, QueryEntity } from '@datorama/akita';
import { memo } from 'helpful-decorators';
import { round } from 'lodash-es';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TimelineQuery } from '@models/milestone-category/milestone/timeline.query';

import { listTimelineMilestonesQuery } from '@shared/services/gql.service';

import { CategoryModel, CategoryState, CategoryStore, CategoryUIState } from './category.store';
import { ActivityQuery } from '../activity/activity.query';
import { ActivityModel } from '../activity/activity.store';
import { ForecastSettingsQuery } from '../settings/forecast-settings.query';
import { ForecastSettingsModel } from '../settings/forecast-settings.store';

export interface FullActivity extends ActivityModel {
  primary_settings: FullSettings;
  secondary_settings: FullSettings;
}

export interface FullCategory extends CategoryModel {
  activities: FullActivity[];
  primary_settings: FullSettings;
  secondary_settings: FullSettings;
}

export interface FullSettings extends Omit<ForecastSettingsModel, 'milestone_category'> {
  end_milestone: null | { id: string; name: string };
  milestone_category: null | { id: string; name: string };
  start_milestone: null | { id: string; name: string };
}

interface ForecastFilters {
  hideActivitiesWithNoRemainingCost: boolean;
  hideForecastedActivities: boolean;
}

@Injectable({ providedIn: 'root' })
export class CategoryQuery extends QueryEntity<CategoryState> {
  private activityQuery = inject(ActivityQuery);
  private forecastSettingsQuery = inject(ForecastSettingsQuery);
  private timelineQuery = inject(TimelineQuery);

  protected store: CategoryStore;

  filters$ = combineLatest([
    this.store.hideActivitiesWithNoRemainingCost$,
    this.store.hideForecastedActivities$,
  ]).pipe(
    map((arr) => {
      return {
        hideActivitiesWithNoRemainingCost: arr[0],
        hideForecastedActivities: arr[1],
      };
    })
  );

  selectTopCategories$: Observable<FullCategory[]> = combineQueries([
    this.selectAll(),
    this.forecastSettingsQuery.selectAll({ asObject: true }),
    this.activityQuery.selectAll({ asObject: true }),
    this.timelineQuery.select('items'),
    this.filters$,
  ]).pipe(
    map(([categories, settings, activities, timelineMilestones, filters]) => {
      return this.filterFullCategory(
        categories.map((category) => {
          return this.categoryMapper([category, settings, activities, timelineMilestones]);
        }),
        filters
      );
    })
  );
  topCategories$ = this.selectAll({ filterBy: (entity) => entity.parent_category_id === null });
  ui!: EntityUIQuery<CategoryUIState>;

  // eslint-disable-next-line max-lines-per-function
  categoryMapper([category, settings, activity, timelineMilestones]: [
    CategoryModel,
    HashMap<ForecastSettingsModel>,
    HashMap<ActivityModel>,
    listTimelineMilestonesQuery[],
  ]) {
    const primary_setting = settings[category.primary_settings_id];
    const secondary_setting =
      settings[category.secondary_settings_id] || ({} as ForecastSettingsModel);
    const mils = timelineMilestones.reduce(
      (acc, val) => {
        acc[val.milestone.id] = val;

        return acc;
      },
      {} as Record<string, listTimelineMilestonesQuery>
    );
    const categories = timelineMilestones.reduce(
      (acc, val) => {
        acc[val.milestone.milestone_category_id] = {
          ...val.milestone.milestone_category,
          id: val.milestone.milestone_category_id,
        };

        return acc;
      },
      {} as Record<string, { id: string; name: string }>
    );
    const fullCategory: FullCategory = {
      ...category,
      activities: category.activity_ids.map((act_id) => {
        const act_primary_settings = settings[activity[act_id].primary_settings_id];
        const act_secondary_settings =
          settings[activity[act_id].secondary_settings_id] || ({} as ForecastSettingsModel);
        const fullActivity: FullActivity = {
          ...activity[act_id],
          primary_settings: {
            ...act_primary_settings,
            end_milestone: act_primary_settings?.period_end_milestone_id
              ? mils[act_primary_settings.period_end_milestone_id]?.milestone
              : null,
            milestone_category: act_primary_settings?.milestone_category
              ? categories[act_primary_settings.milestone_category]
              : null,
            start_milestone: act_primary_settings?.period_start_milestone_id
              ? mils[act_primary_settings.period_start_milestone_id]?.milestone
              : null,
          },
          secondary_settings: {
            ...act_secondary_settings,
            end_milestone: act_secondary_settings?.period_end_milestone_id
              ? mils[act_secondary_settings.period_end_milestone_id]?.milestone
              : null,
            milestone_category: act_secondary_settings?.milestone_category
              ? categories[act_secondary_settings.milestone_category]
              : null,
            start_milestone: act_secondary_settings?.period_start_milestone_id
              ? mils[act_secondary_settings.period_start_milestone_id]?.milestone
              : null,
          },
        };

        return fullActivity;
      }),
      primary_settings: {
        ...primary_setting,
        end_milestone: primary_setting?.period_end_milestone_id
          ? mils[primary_setting.period_end_milestone_id]?.milestone
          : null,
        milestone_category: primary_setting?.milestone_category
          ? categories[primary_setting.milestone_category]
          : null,
        start_milestone: primary_setting?.period_start_milestone_id
          ? mils[primary_setting.period_start_milestone_id]?.milestone
          : null,
      },
      secondary_settings: {
        ...secondary_setting,
        end_milestone: secondary_setting?.period_end_milestone_id
          ? mils[secondary_setting.period_end_milestone_id]?.milestone
          : null,
        milestone_category: secondary_setting?.milestone_category
          ? categories[secondary_setting.milestone_category]
          : null,
        start_milestone: secondary_setting?.period_start_milestone_id
          ? mils[secondary_setting.period_start_milestone_id]?.milestone
          : null,
      },
    };

    return fullCategory;
  }

  constructor() {
    const store = inject(CategoryStore);

    super(store);
    this.store = store;

    this.createUIQuery();
  }

  filterActivitiesAndSubCategories(cat: FullCategory, filters: ForecastFilters) {
    const activities = cat.activities.filter((act) =>
      this.filterCatOrAct(act.primary_settings, filters)
    );
    const sub_categories = cat.sub_category_ids.filter((cat_id) => {
      const sub_cat = this.getEntity(cat_id);
      const primary_settings = this.forecastSettingsQuery.getEntity(
        sub_cat?.primary_settings_id || ''
      );

      if (primary_settings) {
        return this.filterCatOrAct(primary_settings as unknown as FullSettings, filters);
      }

      return false;
    });

    return { ...cat, activities, sub_categories };
  }

  filterCatOrAct(settings: FullSettings, filters: ForecastFilters) {
    const { unforecasted } = this.forecastSettingsQuery.ui.getEntity(settings.id) || {};

    if (filters.hideForecastedActivities && !unforecasted) {
      return false;
    }

    if (filters.hideActivitiesWithNoRemainingCost) {
      const remaining = round(settings.total_cost - settings.total_wp, 2);

      if (!remaining) {
        return false;
      }
    }

    return true;
  }

  filterFullCategory(cats: FullCategory[], filters: ForecastFilters) {
    return cats
      .filter((cat) => this.filterCatOrAct(cat.primary_settings, filters))
      .map((cat) => this.filterActivitiesAndSubCategories(cat, filters));
  }

  @memo()
  selectCategory(cat_id: string): Observable<FullCategory | null> {
    return combineQueries([
      this.selectEntity(cat_id),
      this.forecastSettingsQuery.selectAll({ asObject: true }),
      this.activityQuery.selectAll({ asObject: true }),
      this.timelineQuery.select('items'),
      this.filters$,
    ]).pipe(
      map(([category, settings, activity, timelineMilestones, filters]) => {
        if (category) {
          return this.filterActivitiesAndSubCategories(
            this.categoryMapper([category, settings, activity, timelineMilestones]),
            filters
          );
        }

        return null;
      })
    );
  }
}
